import { useId, useRef, useState } from "react";
import {
  SkyButton,
  SkyDialog,
  SkyInput,
  SkyMessageBar,
  SkyRadioGroup,
  SkyTextarea,
} from "@skypointcloud/skypoint-ui";
import { SectionLoader } from "@/components/shared/Loading";
import { employeeErrorMessage, useGetEmployeeLikersQuery } from "@/store/api/employeeApi";
import { EmployeeAvatar } from "../ui/EmployeeAvatar";
import { REPORT_REASON_LABELS } from "./feedLabels";
import {
  EMP_REPORT_REASONS,
  REPORT_DETAILS_MAX,
  type EmpReportReason,
  type EmpReportTarget,
} from "../../../../shared/employee/moderation";
import type { EmpPersonRef } from "../../../../shared/employee/feed";

/** The server's reason, or a plain fallback. */
export function feedErrorText(error: unknown, fallback: string): string {
  return employeeErrorMessage(error) ?? fallback;
}

/**
 * Wraps SkyDialog's primary action so the dialog stays open, with the reason
 * on screen, when the action fails.
 */
function useStayOpenOnFailure() {
  const ok = useRef(false);
  return {
    run: async (action: () => Promise<boolean>) => {
      ok.current = await action();
    },
    onBeforeClose: (reason: string) => reason !== "primaryAction" || ok.current,
  };
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => Promise<boolean>;
  onClose: () => void;
}

/** "Delete this post?" and the like. */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const stay = useStayOpenOnFailure();
  return (
    <SkyDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onBeforeClose={stay.onBeforeClose}
      size="small"
      title={title}
      secondaryAction={{ label: "Cancel", onClick: onClose }}
      primaryAction={{
        label: confirmLabel,
        destructive: true,
        loading: busy,
        disabled: busy,
        onClick: () =>
          stay.run(async () => {
            setBusy(true);
            try {
              return await onConfirm();
            } finally {
              setBusy(false);
            }
          }),
      }}
    >
      <p className="text-sm text-gray-700 dark:text-gray-300">{message}</p>
    </SkyDialog>
  );
}

export interface ReportDialogProps {
  open: boolean;
  target: EmpReportTarget;
  onSend: (reason: EmpReportReason, details: string) => Promise<string | null>;
  onClose: () => void;
}

/** Report a post or comment: a reason and optional details. */
export function ReportDialog({ open, target, onSend, onClose }: ReportDialogProps) {
  const [reason, setReason] = useState<EmpReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const stay = useStayOpenOnFailure();
  return (
    <SkyDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onBeforeClose={stay.onBeforeClose}
      title={`Report this ${target}`}
      description="Reports go to administrators and the manager responsible. The author isn't told who reported it."
      secondaryAction={{ label: "Cancel", onClick: onClose }}
      primaryAction={{
        label: "Send report",
        loading: busy,
        // Nothing chosen for them: the reason is the reporter's own.
        disabled: busy || !reason,
        onClick: () =>
          stay.run(async () => {
            if (!reason) return false;
            setBusy(true);
            setError(null);
            const failure = await onSend(reason, details.trim());
            setBusy(false);
            setError(failure);
            return failure === null;
          }),
      }}
    >
      <div className="flex flex-col gap-4">
        {error && (
          <SkyMessageBar
            intent="error"
            message={error}
          />
        )}
        <SkyRadioGroup
          label="What's wrong with it?"
          layout="vertical"
          options={EMP_REPORT_REASONS.map((r) => ({ value: r, label: REPORT_REASON_LABELS[r] }))}
          value={reason ?? ""}
          onChange={(next) => setReason(next as EmpReportReason)}
        />
        <SkyTextarea
          label="Details"
          rows={3}
          maxLength={REPORT_DETAILS_MAX}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Anything that helps the reviewer"
        />
      </div>
    </SkyDialog>
  );
}

export interface EditTextDialogProps {
  open: boolean;
  title: string;
  initialText: string;
  /** Shown for announcements and spotlights. */
  initialHeadline?: string | null;
  maxLength: number;
  onSave: (text: string, headline: string | null) => Promise<string | null>;
  onClose: () => void;
}

/** Edit the text of your own post or comment. */
export function EditTextDialog({
  open,
  title,
  initialText,
  initialHeadline,
  maxLength,
  onSave,
  onClose,
}: EditTextDialogProps) {
  const [text, setText] = useState(initialText);
  const [headline, setHeadline] = useState(initialHeadline ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const stay = useStayOpenOnFailure();
  const textId = useId();
  const withHeadline = initialHeadline !== undefined;
  return (
    <SkyDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onBeforeClose={stay.onBeforeClose}
      title={title}
      secondaryAction={{ label: "Cancel", onClick: onClose }}
      primaryAction={{
        label: "Save changes",
        loading: busy,
        disabled: busy || !text.trim(),
        onClick: () =>
          stay.run(async () => {
            setBusy(true);
            setError(null);
            const failure = await onSave(text, withHeadline ? headline.trim() || null : null);
            setBusy(false);
            setError(failure);
            return failure === null;
          }),
      }}
    >
      <div className="flex flex-col gap-3">
        {error && (
          <SkyMessageBar
            intent="error"
            message={error}
          />
        )}
        {withHeadline && (
          <SkyInput
            label="Headline"
            value={headline}
            maxLength={160}
            onChange={(e) => setHeadline(e.target.value)}
          />
        )}
        <label
          htmlFor={textId}
          className="sr-only"
        >
          Text
        </label>
        <SkyTextarea
          id={textId}
          required
          rows={6}
          maxLength={maxLength}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
    </SkyDialog>
  );
}

export interface LikersDialogProps {
  open: boolean;
  target: "post" | "comment";
  id: string;
  count: number;
  onClose: () => void;
}

/** Who liked a post or comment (P3-5). */
export function LikersDialog({ open, target, id, count, onClose }: LikersDialogProps) {
  const [pages, setPages] = useState<(string | undefined)[]>([undefined]);
  return (
    <SkyDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      size="small"
      title={count === 1 ? "1 like" : `${count} likes`}
      showCloseButton
    >
      <ul className="flex flex-col gap-2">
        {pages.map((before, i) => (
          <LikersPage
            key={before ?? "first"}
            target={target}
            id={id}
            before={before}
            onMore={i === pages.length - 1 ? (next) => setPages((p) => [...p, next]) : undefined}
          />
        ))}
      </ul>
    </SkyDialog>
  );
}

function LikersPage({
  target,
  id,
  before,
  onMore,
}: {
  target: "post" | "comment";
  id: string;
  before?: string;
  onMore?: (next: string) => void;
}) {
  const { data, isLoading, isError, refetch } = useGetEmployeeLikersQuery({ target, id, before });
  if (isLoading) return <SectionLoader label="Loading…" />;
  if (isError || !data) {
    return (
      <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        Couldn't load who liked this.
        <SkyButton
          size="small"
          appearance="secondary"
          onClick={() => refetch()}
        >
          Try again
        </SkyButton>
      </li>
    );
  }
  return (
    <>
      {data.items.map((p: EmpPersonRef, i) => (
        <li
          // Someone not named here has no id: keyed by position instead.
          key={p.partyId || `unnamed-${i}`}
          className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100"
        >
          <EmployeeAvatar
            name={p.name}
            photoUrl={p.photoUrl}
            size={28}
          />
          {p.name}
        </li>
      ))}
      {data.nextCursor && onMore && (
        <li>
          <SkyButton
            size="small"
            appearance="subtle"
            onClick={() => onMore(data.nextCursor!)}
          >
            Show more
          </SkyButton>
        </li>
      )}
    </>
  );
}
